const flatMap = require('lodash/flatMap');
const last = require('lodash/last');
const invert = require('lodash/invert');
const zip = require('lodash/zip');
const toPairs = require('lodash/toPairs');

const split = require('./split');
const splitRoute = require('./split-route');

/**
 * RouteMatcher
 */
class RouteMatcher {
  /**
   * @constructor
   * @param {Map<RoutePattern, RouteHandler>} routes Key/value object or map that maps route
   * patterns to their respective handlers
   */
  constructor(routes = {}) {
    const mappings = toPairs(routes);
    const routeDefinitions = mappings.map(([pattern, handler]) => {
      const segments = splitRoute(pattern);
      return { pattern, segments, handler };
    });

    /**
     * Route matching tree, each branch keyed by a route pattern segment
     * @private
     * @type {Branch}
     */
    this.tree = createRouteTree(routeDefinitions);

    /**
     * List of mappings from route patterns to handlers
     * @private
     * @type {Array<Array>}
     * @example
     * [
     *   ['foo[{keys}]', {...}],
     *   ['foo.bar', {...}],
     *   ['foo.*', {...}],
     * ]
     */
    this.mappings = mappings;

    /**
     * Sort cache metadata
     * @private
     * @type {Array<Object>}
     * @example
     * [
     *   { pattern: 'foo.bar', segments: ['foo', 'bar'], handler: Function },
     *   { pattern: 'foo[{keys}]', segments: ['foo', '{keys}'], handler: Function },
     *   { pattern: 'foo.*', segments: ['foo', '*'], handler: Function },
     * ]
     */
    this.sortedRoutes = getSortedRoutes(routeDefinitions);

    /**
     * Array mapping route patterns to sort index
     * @private
     * @type {Map<Path,number>}
     * @example
     * {
     *   'foo.bar': 0,
     *   'foo[{keys}]': 1,
     *   'foo.*': 2,
     * }
     */
    this.routeSortIndices = getSortIndices(this.sortedRoutes);
  }

  /**
   * Add a route handler pattern mapping
   * @param {RoutePattern} pattern Route pattern
   * @param {RouteHandler} handler Route handler to map to the specified pattern
   * @return {RouteHandler} The route handler
   */
  set(pattern, handler) {
    const routeDefinition = { pattern, handler, segments: splitRoute(pattern) };
    this.tree = addRouteToTree(this.tree, routeDefinition);
    this.mappings.push([pattern, handler]);
    this.sortedRoutes = getUpdatedSortedRoutes(this.sortedRoutes, routeDefinition);
    this.routeSortIndices = getSortIndices(this.sortedRoutes);
    return handler;
  }

  /**
   * Get the handler that corresponds to a path
   * @param {Path} path Path to match
   * @return {RouteHandler|undefined} RouteHandler corresponding to the specified path
   */
  get(path) {
    const matches = this.getSortedMatches(path);
    return matches.length === 0 ? undefined : matches[0].handler;
  }

  /**
   * Determine whether there is a graph node that corresponds to the specified path
   * @param {Path} path Path to match
   * @return {boolean} Whether there is a graph node corresponding to the specified path
   */
  has(path) {
    const matches = getBranchOrLeafMatches(this.tree, path);
    return (matches.length > 0);
  }

  /**
   * Get the branches that match a path in order of precedence
   * @param {Path} path Path to match
   * @return {Array<Branch>|undefined} The matched branches
   */
  getSortedMatches(path) {
    const matches = getLeafMatches(this.tree, path);
    if (matches.length <= 1) { return matches; }
    const routeSortIndices = this.routeSortIndices;
    const sortedMatches = matches.sort(({ pattern: pattern1 }, { pattern: pattern2 }) =>
      (routeSortIndices[pattern1] - routeSortIndices[pattern2])
    );
    return sortedMatches;
  }

  /**
   * Get a list of registered route patterns
   * @return {Array<RoutePattern>} Array containing registered route patterns
   */
  keys() {
    return this.mappings.map(([pattern]) => pattern);
  }

  /**
   * Get a list of registered route handlers
   * @return {Array<RouteHandler>} Array containing registered route patterns
   */
  values() {
    return this.mappings.map(([, handler]) => handler);
  }

  /**
   * Get a list of route pattern / route handler mappings
   * @return {Array<Array>} Array mapping route patterns to route handlers
   */
  entries() {
    return this.mappings.slice();
  }
}

module.exports = RouteMatcher;

/**
 * @private
 * Routes are stored in a tree structure for fast matching lookups.
 *
 * For example, the following route set:
 *
 *    foo.bar.baz
 *    foo.bar.qux
 *    foo.items[{keys}]
 *    bar.*
 *
 * ...can be represented in a tree as follows:
 *
 *   root            (root branch)
 *   ├─┬ foo         (static branch)
 *   │ ├─┬ bar       (static branch)
 *   │ │ ├── baz     (static branch)
 *   │ │ └── qux     (static branch)
 *   │ └─┬ items     (static branch)
 *   │   └── {keys}  ('all keys' branch)
 *   └─┬ bar         (static branch)
 *     └── *         (wildcard branch)
 *
 * As seen above, branches can either be 'static' branches, or 'dynamic' branches that match
 * multiple queries.
 *
 * This class defines the root branch type, with various subclasses that define the various types of
 * branches and their corresponding path matching rules.
 */
class Branch {
  constructor() {
    /**
     * Child branches
     * @type {Array<Branch>}
     */
    this.children = [];

    /**
     * Branch value
     * @type {any}
     */
    this.value = undefined;
  }

  /**
   * Set the branch value
   * @param {any} value Branch value
   * @returns {any} Updated branch value
   */
  setValue(value) {
    return (this.value = value);
  }

  /**
   * Check whether the branch has a value
   * @returns {boolean} Whether the branch has a value
   */
  hasValue() {
    return (this.value !== undefined);
  }

  /**
   * Get or create a child branch for the specified matching pattern
   * @param {string|number|object} pattern Pattern to use as the key for the child branch
   * @returns {Branch} Newly-created child branch (or existing branch if one already existed)
   */
  createChildBranch(pattern) {
    const existingChildBranch = this.children.find((child) => child.is(pattern));
    if (existingChildBranch) { return existingChildBranch; }
    const childBranch = createBranch(pattern);
    this.children.push(childBranch);
    return childBranch;
  }

  /**
   * Get this node's child branches that match the specified path key
   * @param {string|number|object} key Path segment
   * @returns {Array<Branch>} Branches that match the specified path key
   */
  getMatchingBranches(key) {
    return this.children.filter((childBranch) => childBranch.test(key));
  }

  /**
   * Determine whether the branch represents the specified matching pattern
   * @param {string|number|object} pattern Pattern to test
   * @return {boolean} Whether the branch is based on the specified matching pattern
   */
  is(pattern) { // eslint-disable-line no-unused-vars
    return false;
  }

  /**
   * Determine whether the branch matches a path key
   * @param {string} key Path segment to test
   * @returns {boolean} Whether the branch matches the key
   */
  test(key) { // eslint-disable-line no-unused-vars
    return true;
  }

  /**
   * A string representation of this Branch as an example,
   * useful for devtools
   * @returns {string} String representation of the branch
   */
  toString() {
    return '';
  }
}

function isKey(pathSegment) {
  return (typeof pathSegment === 'string') && !isWildcard(pathSegment);
}

function isInteger(pathSegment) {
  return (typeof pathSegment === 'number');
}

function isRange(pathSegment) {
  return (typeof pathSegment === 'object') &&
    (typeof pathSegment.from === 'number') && (typeof pathSegment.to === 'number');
}

function isAllKeys(pathSegment) {
  return (typeof pathSegment === 'object') && (pathSegment.type === 'keys');
}

function isAllIntegers(pathSegment) {
  return (typeof pathSegment === 'object') && (pathSegment.type === 'integers');
}

function isWildcard(pathSegment) {
  return pathSegment === '*';
}

function isVariable(pathSegment) {
  return isAllIntegers(pathSegment) || isAllKeys(pathSegment) || isWildcard(pathSegment);
}


class KeyBranch extends Branch {
  constructor(key) {
    super();
    this.key = key;
  }

  is(pattern) {
    return isKey(pattern) && (pattern === this.key);
  }

  test(key) {
    return key === this.key;
  }

  toString() {
    return this.key;
  }
}

class IntegerBranch extends Branch {
  constructor(index) {
    super();
    this.index = index;
  }

  is(pattern) {
    return isInteger(pattern) && (pattern === this.index);
  }

  test(index) {
    return index === this.index;
  }

  toString() {
    return this.index;
  }
}

class RangeBranch extends Branch {
  constructor({ from, to }) {
    super();
    this.from = from;
    this.to = to;
  }

  is(pattern) {
    return isRange(pattern) && (pattern.from === this.from) && (pattern.to === this.to);
  }

  test(index) {
    return (typeof index === 'number') && (index >= this.from) && (index <= this.to);
  }

  toString() {
    return `[${this.from}..${this.to}]`;
  }
}

class AllIntegersBranch extends Branch {
  is(pattern) {
    return isAllIntegers(pattern);
  }

  test(index) {
    return isInteger(index);
  }

  toString() {
    return '0';
  }
}

class AllKeysBranch extends Branch {
  is(pattern) {
    return isAllKeys(pattern);
  }

  test(index) {
    return isKey(index);
  }

  toString() {
    return 'KEY';
  }
}

class WildcardBranch extends Branch {
  constructor() {
    super();
    this.children.push(this);
  }

  is(pattern) {
    return isWildcard(pattern);
  }

  test() {
    return true;
  }

  createChildBranch() {
    return this;
  }

  getMatchingBranches() {
    return [this];
  }

  toString() {
    return '*';
  }
}

// Lookup table for creating different branch types based on route pattern segment
const BRANCH_FACTORIES = [
  { test: isKey, factory: (key) => new KeyBranch(key) },
  { test: isInteger, factory: (index) => new IntegerBranch(index) },
  { test: isRange, factory: (range) => new RangeBranch(range) },
  { test: isAllKeys, factory: () => new AllKeysBranch() },
  { test: isAllIntegers, factory: () => new AllIntegersBranch() },
  { test: isWildcard, factory: () => new WildcardBranch() },
];

function createBranch(pattern) {
  const factory = BRANCH_FACTORIES.find(({ test }) => test(pattern)).factory;
  const branch = factory(pattern);
  return branch;
}

function createRouteTree(routeDefinitions) {
  return routeDefinitions.reduce((tree, routeDefinition) =>
      addRouteToTree(tree, routeDefinition),
    new Branch()
  );
}

function addRouteToTree(tree, { pattern, segments, handler }) {
  const leaves = segments.reduce(createBranchesForSegment, [tree]);
  leaves.forEach((leaf) => {
    if (leaf.hasValue()) {
      const existingPattern = leaf.value.pattern;
      throw new Error(`Handler already exists for ${formatPath(pattern)}` +
        ` (defined in ${formatPath(existingPattern)})`
      );
    }
    leaf.setValue({ pattern, handler });
  });
  return tree;

  function createBranchesForSegment(parentBranches, pathSegment) {
    const branchMatchesMultiplePatterns = Array.isArray(pathSegment);
    if (branchMatchesMultiplePatterns) {
      return flatMap(pathSegment, (segment) => createBranchesForSegment(parentBranches, segment));
    }
    return parentBranches.map((parentBranch) => parentBranch.createChildBranch(pathSegment));
  }
}

function getSortedRoutes(routeDefinitions) {
  return routeDefinitions.map((routeDefinition) =>
    Object.assign({}, routeDefinition, {
      score: calculatePatternSortScores(routeDefinition.segments),
    })
  )
    .sort(({ score: score1 }, { score: score2 }) => compareSortScores(score1, score2));
}

function getUpdatedSortedRoutes(sortedRoutes, routeDefinition) {
  const routeSortData = Object.assign({}, routeDefinition, {
    score: calculatePatternSortScores(routeDefinition.segments),
  });
  const sortIndex = getSortIndex(sortedRoutes, routeSortData.score);
  const updatedSortedRoutes = sortedRoutes.slice();
  updatedSortedRoutes.splice(sortIndex, 0, routeSortData);
  return updatedSortedRoutes;
}

function getSortIndex(sortedRoutes, score) {
  const index = sortedRoutes.findIndex(({ score: existingScore }) =>
    compareSortScores(score, existingScore) <= 0
  );
  return (index === -1 ? sortedRoutes.length : index);
}

function compareSortScores(scores1, scores2) {
  // Compare the two patterns in each score category until one comes out ahead
  return zip(scores1, scores2).reduce((sortResult, [score1, score2]) =>
      (sortResult !== 0 ? sortResult : score2 - score1)
    , 0);
}

function getSortIndices(sortedRoutes) {
  return invert(sortedRoutes.map(({ pattern }) => pattern));
}

// Define the sort precedence of different types of branch (lowest to highest)
const BRANCH_TYPE_PRIORITIES = [
  isWildcard,
  (pattern) => isAllIntegers(pattern) || isAllKeys(pattern),
  (pattern) => isRange(pattern),
  (pattern) => isInteger(pattern) || isKey(pattern),
];

function calculatePatternSortScores(pathSegments) {
  /*
   Route patterns are scored in several categories, defined as follows (most important first):

   1. If one pattern has fewer segments than the other (due to wildcards), the longer one wins
   2. If one pattern ends in a wildcard, the non-wildcard pattern wins
   3. If one pattern has fewer [{integers}] and [{keys}] variables than the other, it wins
   4. The patterns are split into their respective segments and scored rightmost-first according
   to the branch type precedence rules.

   Individual segments are scored as follows (highest-scoring to lowest-scoring):

   - Static integer or key (e.g. 'foo' or [3])
   - Integer range (e.g. [0..3])
   - [{integers}] or [{keys}] variable
   - Wildcard ('*')
   */
  return [
    pathSegments.length, // [1]
    isWildcard(last(pathSegments)) ? -1 : 0, // [2]
    -pathSegments.filter((pathSegment) => isVariable(pathSegment)).length, // [3]
    ...pathSegments.map((pathSegment) => calculatePatternSegmentScore(pathSegment)).reverse(), //[4]
  ];

  function calculatePatternSegmentScore(pathSegment) {
    return BRANCH_TYPE_PRIORITIES.findIndex((test) => test(pathSegment));
  }
}

function getBranchOrLeafMatches(tree, path) {
  return split(path).reduce((matchingBranches, pathSegment) =>
      // Multiple branches can match at each tree level, so merge all matches into the same list
      flatMap(matchingBranches, (branch) => branch.getMatchingBranches(pathSegment)),
    [tree]
  );
}

function getLeafMatches(tree, path) {
  return getBranchOrLeafMatches(tree, path)
    .filter((branch) => {
      const isLeafBranch = branch.hasValue();
      return isLeafBranch;
    })
    .map((branch) => branch.value);
}

function formatPath(path) {
  return JSON.stringify(path).replace(/\\"/g, '"').replace(/^"|"$/g, '');
}
